import { HttpStatusCode } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Event, NavigationEnd, Router } from '@angular/router';
import { FeatureFlags } from '@core/enumeration';
import { BusinessTypeIDs } from '@core/model';
import { RouterFragments } from '@core/model/RouterFragments';
import {
  AuthenticationService,
  AuthorizationDmeposService,
  AuthorizationPacimService,
  AuthorizationService,
  BroadcastAppService,
  TenantService,
} from '@core/service';
import { AlphaNum } from '@shared/constants';
import { PortalProducts } from '@shared/enumeration';
import { AuthNumberAssertion } from '@shared/utility';
import { BusinessType, DateUtilities, SecurityService, Tenant, TenantNames, noWhiteSpaceValidator } from '@mynexus/mynexus-ui-lib';
import { ShellService } from '@mynexus/mfe-core';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { AccordionDesignTokens } from '@primeuix/themes/types/accordion';
import { AccordionHeaderPassThrough } from 'primeng/accordion';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'general-search-panel',
    templateUrl: './general-search-panel.component.html',
    styleUrls: ['./general-search-panel.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class GeneralSearchPanelComponent implements OnInit {
  tenantList: Tenant[] = [];
  selectedTenant: Tenant = null;
  isExternalUser: boolean;
  activeTenantName: string;
  tenantExpanded = true;
  isSelectHPError = false;
  searchForm: FormGroup;
  searchClaimForm: FormGroup;
  searchAuthForm: FormGroup;
  memberExpanded = false;
  authExpanded = false;
  yearOfBirthRange = `1900:${new Date().getFullYear()}`;
  birthDateMaxSelectableDate: Date = new Date();
  featureFlags = FeatureFlags;

  menu: string;
  readonly TenantEmptyValue = 'none';
  readonly AuthDashboard = 'auth-dashboard';
  userBusinessType: BusinessType = null;
  activeTenantId: string;
  private subs = new SubSink();

  private isSearchFormExtended!: boolean;

  readonly dt: AccordionDesignTokens = {
    panel: {
      borderWidth: '2px',
      borderColor: 'transparent',
    },
    header: {
      padding: '0.625rem',
      hoverBackground: '{purple.200}',
      activeColor: '{surface.0}',
      activeBackground: '{purple.400}',
      activeHoverBackground: '{purple.300}',
      activeHoverColor: '{surface.0}',
      borderWidth: '0',
      borderRadius: '4px',
      toggleIcon: {
        activeColor: '{surface.0}',
        activeHoverColor: '{surface.0}'
      },
      first: {
        borderWidth: '0',
      }
    },
    content: {
      borderWidth: '0',
      background: 'transparent',
      padding: '1rem 0'
    }
  }

  readonly pt: AccordionHeaderPassThrough = {
    root: {
      class: 'justify-start!'
    },
    toggleicon: { class: 'order-first mr-3' }
  }

  get shouldShowExtendedSearchFields(): boolean {
    return this.isSearchFormExtended;
  }

  constructor(
    private readonly ref: ChangeDetectorRef,
    private readonly router: Router,
    private activatedRoute: ActivatedRoute,
    private readonly authenticationService: AuthenticationService,
    private readonly tenantService: TenantService,
    private readonly broadcastAppService: BroadcastAppService,
    private readonly securityService: SecurityService,
    private readonly authorizationService: AuthorizationService,
    private readonly authorizationPacimService: AuthorizationPacimService,
    private readonly authorizationDmeposService: AuthorizationDmeposService,
    private readonly shellService: ShellService
  ) {
    this.isExternalUser = this.authenticationService.isExternalUser;
  }

  ngOnInit(): void {
    this.isSearchFormExtended = this.isExternalUser
      && !this.authenticationService.userGroups.includes('BillingOfficeExternal');

    this.getTenants();
    this.initForms();

    if (this.activatedRoute.firstChild) {
      this.subs.sink = this.activatedRoute.firstChild.params.subscribe(params => {
        if (!this.activeTenantId) {
          this.activeTenantId = params.tenantId;
        }
        this.ref.markForCheck();
      });
    }

    this.subs.sink = this.router.events.subscribe((event: Event) => {
      if (event instanceof NavigationEnd) {
        this.menu = event.urlAfterRedirects.indexOf('advanced-search') !== -1 ? 'advancedsearch' : '';
        this.ref.markForCheck();
      }
    });

    this.subs.sink = this.broadcastAppService.healthPlan.subscribe((tenantId: string) => {
      if (!this.activeTenantId || tenantId !== this.TenantEmptyValue) {
        this.activeTenantId = tenantId;
      }
    });
  }

  initForms() {
    this.searchForm = new FormGroup({
      memberId: new FormControl('', [Validators.required, Validators.maxLength(50)]),
    });

    if (this.isSearchFormExtended) {
      this.searchForm.addControl(
        'lastName',
        new FormControl('', [
          Validators.required,
          Validators.maxLength(50),
          noWhiteSpaceValidator
        ])
      );
      this.searchForm.addControl(
        'dateOfBirth',
        new FormControl('', [Validators.required])
      );
    }

    this.searchAuthForm = new FormGroup({
      authorizationNumber: new FormControl('', [
        Validators.required
      ]),
    });

    this.searchClaimForm = new FormGroup({
      claimId: new FormControl(''),
      paymentDateToPicker: new FormControl('', [Validators.required]),
      paymentDateFromPicker: new FormControl('', [Validators.required]),
    });
  }


  get memberIdInput(): AbstractControl {
    return this.searchForm.get('memberId');
  }

  get memberLastNameInput(): AbstractControl {
    return this.searchForm.get('lastName');
  }

  get memberDOBInput(): AbstractControl {
    return this.searchForm.get('dateOfBirth');
  }

  get authorizationNumberInput(): AbstractControl {
    return this.searchAuthForm.get('authorizationNumber');
  }

  getTenants() {
    this.subs.sink = this.tenantService
      .getTenants(this.isExternalUser)
      .pipe(
        finalize(() => {
          this.ref.markForCheck();
        })
      )
      .subscribe((tenants) => {
        this.tenantList = tenants;
        if (this.activeTenantId) {
          this.selectedTenant = this.tenantList.find(
            (el) => el.tenantId === this.activeTenantId
          );
        }

        if (this.isExternalUser) {
          this.getUserBusinessType();
        }
      });
  }

  getUserBusinessType() {
    this.subs.sink = this.tenantService
      .getUserBusinessType(this.isExternalUser)
      .subscribe((businessType) => {
        this.userBusinessType = businessType;
        this.ref.markForCheck();
      });
  }

  get canDoClaimsSearch(): boolean {
    if (this.securityService.hasAnyPermissions()) {
      return this.securityService.hasAccess(['Claims.Read']);
    } else {
      return this.userBusinessType &&
        (this.userBusinessType.businessTypeID === BusinessTypeIDs.BillingOffice ||
          this.userBusinessType.businessTypeID === BusinessTypeIDs.AdminHomeHealth
        );
    }
  }

  get canDoSearch(): boolean {
    this.securityService.setAllowEmptyPermission(false);
    const state = this.isExternalUser === false
      || this.securityService.hasAccess(['MemberPage.UI'])
      || (this.userBusinessType &&
        (this.userBusinessType.businessTypeID === BusinessTypeIDs.AdminHomeHealth ||
          this.userBusinessType.businessTypeID === BusinessTypeIDs.HomeHealth ||
          this.userBusinessType.businessTypeID === BusinessTypeIDs.HealthPlanCaseManager ||
          this.userBusinessType.businessTypeID === BusinessTypeIDs.Physician ||
          this.userBusinessType.businessTypeID === BusinessTypeIDs.SDoHProviderAdmin ||
          this.userBusinessType.businessTypeID === BusinessTypeIDs.SDoH
        ));
    this.securityService.setAllowEmptyPermission(true);
    return state;
  }

  onSelectTenant(event: { originalEvent: PointerEvent, value: Tenant}) {
    if (this.broadcastAppService.isNavigationBlocked) {
      const noteMfeService = this.shellService.getMfeService('mnc-notes') as any;
      if (noteMfeService && typeof noteMfeService.requestDeactivationConfirm === 'function') {
        this.subs.sink = noteMfeService.requestDeactivationConfirm().subscribe((canNavigate: boolean) => {
          if (canNavigate) {
            this.proceedWithTenantChange(event.value);
          } else {
            this.selectedTenant = this.tenantList.find(t => t.tenantId === this.activeTenantId);
          }
        });
      } else {
        this.proceedWithTenantChange(event.value);
      }
    } else {
      this.proceedWithTenantChange(event.value);
    }
  }

  private proceedWithTenantChange(tenant: Tenant): void {
    this.activeTenantId = tenant.tenantId;
    this.activeTenantName = tenant.name || tenant.displayName;
    this.broadcastAppService.healthPlan.next(this.activeTenantId);
    // To hide note side bar details table
    this.resetNotesSidebar();
    this.resetActiveMenu();
    this.resetAuthNumberValidators();
  }

  private resetAuthNumberValidators(): void {
    this.authorizationNumberInput.clearValidators();
    if (this.activeTenantName?.localeCompare(TenantNames.Regence, undefined, {sensitivity: 'base'}) === 0) {
      this.authorizationNumberInput.addValidators(
        [Validators.required, Validators.minLength(9), Validators.pattern(AlphaNum)]
      )
    }
    else {
      this.authorizationNumberInput.addValidators(
        [Validators.required, Validators.minLength(15), Validators.pattern(AlphaNum)]
      )
    }
    this.authorizationNumberInput.updateValueAndValidity({ emitEvent: false });
  }

  onPasteAuthNumber($event: ClipboardEvent) {
    const alphaNumRegex = new RegExp(AlphaNum);
    const pastedData = $event.clipboardData.getData('text')?.trim();
    if (pastedData && alphaNumRegex.test(pastedData)) {
      $event.preventDefault();
      this.authorizationNumberInput.setValue(pastedData);
    }
    this.resetAuthNumberValidators();
  }

  private goToAuthPage(
    subscriberNumber: string,
    product: PortalProducts,
    authNumber: string,
    hasAccess?: boolean,
    hasAccessDeniedException?: boolean,
    serverError?: boolean,
    httpErrorCode?: number
  ): void {
    const path = this.getAuthPath(product, subscriberNumber, authNumber, hasAccess);

    if (this.broadcastAppService.isNavigationBlocked) {
      const noteMfeService = this.shellService.getMfeService('mnc-notes') as any;
      if (noteMfeService && typeof noteMfeService.requestDeactivationConfirm === 'function') {
        this.subs.sink = noteMfeService.requestDeactivationConfirm().subscribe((canNavigate: boolean) => {
          if (canNavigate) {
            this.performAuthNavigation(path, hasAccess, hasAccessDeniedException, serverError, httpErrorCode);
          }
        });
      } else {
        this.performAuthNavigation(path, hasAccess, hasAccessDeniedException, serverError, httpErrorCode);
      }
      return;
    }
    this.performAuthNavigation(path, hasAccess, hasAccessDeniedException, serverError, httpErrorCode);
  }

  private performAuthNavigation(
    path: string[],
    hasAccess?: boolean,
    hasAccessDeniedException?: boolean,
    serverError?: boolean,
    httpErrorCode?: number
  ): void {
    this.router.navigate(path, { state: { hasAccess, hasAccessDeniedException, serverError, httpErrorCode } }).then(() => this.resetInputs());
    this.resetNotesSidebar();
    this.resetActiveMenu();
  }

  // We push in this order to match existing routing structure
  private getAuthPath(product: string, subscriberNumber: string, authNumber: string, hasAccess: boolean): string[] {
    const path = ['/', this.activeTenantId];

    if (product) {
      path.push(product);
    }

    path.push(this.AuthDashboard);

    if (product === PortalProducts.HomeHealth) {
      // If product is HH add auth number if user has access else unknown
      path.push(hasAccess ? authNumber : RouterFragments.UNKNOWN);
    } else if ([PortalProducts.PACIM, PortalProducts.PacCommercial, PortalProducts.DMEPOS].includes(product as PortalProducts)) {
      // if PAC or DME, add auth number if it exists or unknown to redirect to product specific auth not found
      path.push(hasAccess && subscriberNumber ? authNumber : RouterFragments.UNKNOWN);
    } else {
      // otherwise add unknown to redirect to general auth not found
      path.push(RouterFragments.UNKNOWN);
    }

    return path;
  }

  resetInputs() {
    this.memberIdInput?.reset();
    this.authorizationNumberInput?.reset();
    this.memberLastNameInput?.reset();
    this.memberDOBInput?.reset();
    this.memberExpanded = false;
    this.authExpanded = false;
  }

  onSearchMember() {
    const memberId = this.memberIdInput.value.trim();

    if (this.broadcastAppService.isNavigationBlocked) {
      const noteMfeService = this.shellService.getMfeService('mnc-notes') as any;
      if (noteMfeService && typeof noteMfeService.requestDeactivationConfirm === 'function') {
        this.subs.sink = noteMfeService.requestDeactivationConfirm().subscribe((canNavigate: boolean) => {
          if (canNavigate) {
            this.performMemberNavigation(memberId);
          }
        });
      } else {
        this.performMemberNavigation(memberId);
      }
      return;
    }

    this.performMemberNavigation(memberId);
  }

  private performMemberNavigation(memberId: string) {
    if (this.isSearchFormExtended) {
      const lastName = this.memberLastNameInput.value.trim();
      const dateOfBirth = DateUtilities.convertToUTCDate(this.memberDOBInput.value).toISOString();

      this.router.navigate(
        ['/', this.activeTenantId, 'member', memberId],
        { queryParams: { lastName, dateOfBirth } }
      )
      .then(() => this.resetInputs());
    } else {
      this.router.navigate(['/', this.activeTenantId, 'member', memberId])
      .then(() => this.resetInputs());
    }

    this.resetNotesSidebar();
    this.resetActiveMenu();
  }

  onSearchAuth(): void {
    const authorizationNumber = String(this.authorizationNumberInput.value).trim();

    if (authorizationNumber) {
      let request: Observable<any> = null;
      let product: PortalProducts = null;
      let hasPermissions = false;

      if (AuthNumberAssertion.isDMEPOS(authorizationNumber) || AuthNumberAssertion.isDMEPOSMCD(authorizationNumber)) {
        if (this.securityService.hasAccess(['DMEPOSAuthorization.Read'])) {
          request = this.authorizationDmeposService.getMemberDataByNumber(this.activeTenantId, authorizationNumber);
          hasPermissions = true;
          product = PortalProducts.DMEPOS;
        }
      } else if (AuthNumberAssertion.isPACIM(authorizationNumber)) {
        if (this.securityService.hasAccess(['PACIMAuthorization.Read'])) {
          if (product === PortalProducts.PACIM && authorizationNumber.length === 9 && this.activeTenantName === TenantNames.Regence) {
            request = this.authorizationPacimService.searchAuthorizationByNumberAndTenant(this.activeTenantId, authorizationNumber);
          } else {
            request = this.authorizationPacimService.getMemberIdByAuthNumber(this.activeTenantId, authorizationNumber);
          }

          hasPermissions = true;
          product = PortalProducts.PACIM;
        }
      } else if (AuthNumberAssertion.isPACCommercial(authorizationNumber)) {
        if (this.securityService.hasAccess(['PACIMAuthorization.Read'])) {
          request = this.authorizationPacimService.getMemberIdByAuthNumber(this.activeTenantId, authorizationNumber);
          hasPermissions = true;
          product = PortalProducts.PacCommercial;
        }
      } else {
        if (this.securityService.hasAccess(['AuthorizationPage.UI'])) {
          request = this.authorizationService.getMemberIdByAuthNumber(this.activeTenantId, authorizationNumber);
          hasPermissions = true;
          product = PortalProducts.HomeHealth;
        }
      }

      if (request && hasPermissions && product) {
        this.subs.sink = request.subscribe((res) => {
          const hasAccess = hasPermissions ? res && !(res?.errorCode && res?.errorCode != HttpStatusCode.NotFound) : hasPermissions;
          const hasAccessDeniedException = res && res?.reportCode === 'General-123';
          const serverError = res && res?.errorCode >= 500;
          const httpErrorCode = res && res?.errorCode;

          this.goToAuthPage(res?.subscriberNumber, product, authorizationNumber, hasAccess, hasAccessDeniedException, serverError, httpErrorCode);
        });
      } else {
        // General error navigation
        this.goToAuthPage(null, product, RouterFragments.UNKNOWN, false, false, false, 403);
      }
    }
  }

  resetNotesSidebar() {
    this.broadcastAppService.broadcastNotes(false, undefined, undefined);
  }

  resetActiveMenu() {
    this.broadcastAppService.broadcast(null, null);
  }

  viewingProviderDashboard() {
    return this.router.url.indexOf('provider-dashboard') !== -1;
  }

  goToProviderDashboard() {
    if (this.broadcastAppService.isNavigationBlocked) {
      const noteMfeService = this.shellService.getMfeService('mnc-notes') as any;
      if (noteMfeService && typeof noteMfeService.requestDeactivationConfirm === 'function') {
        this.subs.sink = noteMfeService.requestDeactivationConfirm().subscribe((canNavigate: boolean) => {
          if (canNavigate) {
            this.resetNotesSidebar();
            this.resetActiveMenu();
            const fullUrl = `${this.activeTenantId}/dmepos/provider-dashboard`;
            this.router.navigate([fullUrl]);
          }
        });
      } else {
        const fullUrl = `${this.activeTenantId}/dmepos/provider-dashboard`;
        this.router.navigate([fullUrl]);
        this.resetNotesSidebar();
        this.resetActiveMenu();
      }
      return;
    }
    
    const fullUrl = `${this.activeTenantId}/dmepos/provider-dashboard`;
    this.router.navigate([fullUrl]);
    this.resetNotesSidebar();
    this.resetActiveMenu();
  }

  goToAdvancedSearchPage() {
    if (this.broadcastAppService.isNavigationBlocked) {
      const noteMfeService = this.shellService.getMfeService('mnc-notes') as any;
      if (noteMfeService && typeof noteMfeService.requestDeactivationConfirm === 'function') {
        this.subs.sink = noteMfeService.requestDeactivationConfirm().subscribe((canNavigate: boolean) => {
          if (canNavigate) {
            this.resetNotesSidebar();
            this.resetActiveMenu();
            this.router.navigate(['/', 'advanced-search']);
          }
        });
      } else {
        this.router.navigate(['/', 'advanced-search']);
        this.resetNotesSidebar();
        this.resetActiveMenu();
      }
      return;
    }
    this.router.navigate(['/', 'advanced-search']);
    this.resetNotesSidebar();
    this.resetActiveMenu();
  }
}
